import { useCallback, useEffect, useMemo, useState } from 'react';

const normalizeText = (value) => String(value ?? '').trim().toLowerCase();

const columnMinWidth = (column) => Math.max(72, Number(column?.minWidth || 72));

const readPreferences = (storageKey) => {
  if (!storageKey) return null;

  try {
    const value = JSON.parse(localStorage.getItem(storageKey) || 'null');
    return value && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
};

const getColumnValue = (column, item) => {
  if (typeof column?.sortValue === 'function') return column.sortValue(item);
  if (typeof column?.accessor === 'function') return column.accessor(item);
  return item?.[column?.id];
};

const matchesFilter = (item, filter, definitionsById, columnsById) => {
  const definition = definitionsById.get(filter.id);
  if (!definition) return true;

  const column = columnsById.get(definition.columnId || definition.id);
  const rawValue = typeof definition.getValue === 'function'
    ? definition.getValue(item)
    : getColumnValue(column, item);

  if (definition.type === 'date-range') {
    if (!filter.startDate && !filter.endDate) return true;
    const date = new Date(rawValue);
    if (Number.isNaN(date.getTime())) return false;

    if (filter.startDate) {
      const start = new Date(`${filter.startDate}T00:00:00`);
      if (date < start) return false;
    }

    if (filter.endDate) {
      const end = new Date(`${filter.endDate}T23:59:59.999`);
      if (date > end) return false;
    }

    return true;
  }

  if (definition.type === 'multi-select') {
    const selected = Array.isArray(filter.values) ? filter.values.map(normalizeText) : [];
    return !selected.length || selected.includes(normalizeText(rawValue));
  }

  if (definition.type === 'select') {
    return !filter.value || normalizeText(rawValue) === normalizeText(filter.value);
  }

  if (definition.type === 'boolean') {
    return filter.value === '' || filter.value === undefined
      ? true
      : Boolean(rawValue) === (filter.value === true || filter.value === 'true');
  }

  return !filter.value || normalizeText(rawValue).includes(normalizeText(filter.value));
};

function useCrmListState({
  items = [],
  columns = [],
  views = [],
  filterDefinitions = [],
  getRowId = (item) => item?.id,
  initialSort,
  initialFilters = {},
  initialPageSize = 20,
  storageKey,
}) {
  const defaultColumnOrder = useMemo(() => columns.map((column) => column.id), [columns]);
  const defaultHiddenColumns = useMemo(
    () => columns.filter((column) => column.defaultHidden).map((column) => column.id),
    [columns]
  );
  const defaultPinnedColumn = null;
  const [search, setSearch] = useState('');
  const [activeView, setActiveView] = useState(views[0]?.id || 'all');
  const [filters, setFilters] = useState(() => ({ ...(initialFilters || {}) }));
  const [sort, setSort] = useState(initialSort || null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [columnPreferences, setColumnPreferences] = useState(() => {
    const stored = readPreferences(storageKey);
    return {
      order: Array.isArray(stored?.order) ? stored.order : [],
      hidden: Array.isArray(stored?.hidden) ? stored.hidden : defaultHiddenColumns,
      pinned: stored ? stored.pinned || null : defaultPinnedColumn,
      widths: stored?.widths && typeof stored.widths === 'object' ? stored.widths : {},
    };
  });

  const columnsById = useMemo(
    () => new Map(columns.map((column) => [column.id, column])),
    [columns]
  );
  const definitionsById = useMemo(
    () => new Map(filterDefinitions.map((definition) => [definition.id, definition])),
    [filterDefinitions]
  );

  useEffect(() => {
    setColumnPreferences((previous) => {
      const validOrder = previous.order.filter((id) => columnsById.has(id));
      const missing = defaultColumnOrder.filter((id) => !validOrder.includes(id));
      const next = {
        order: [...validOrder, ...missing],
        hidden: previous.hidden.filter((id) => (
          columnsById.has(id) && columnsById.get(id)?.hideable !== false
        )),
        pinned: columnsById.has(previous.pinned) ? previous.pinned : null,
        widths: Object.fromEntries(
          Object.entries(previous.widths || {}).filter(([id]) => columnsById.has(id))
        ),
      };

      if (
        next.order.join('|') === previous.order.join('|') &&
        next.hidden.join('|') === previous.hidden.join('|') &&
        next.pinned === previous.pinned &&
        JSON.stringify(next.widths) === JSON.stringify(previous.widths || {})
      ) {
        return previous;
      }

      return next;
    });
  }, [columnsById, defaultColumnOrder]);

  useEffect(() => {
    if (!storageKey || !columnPreferences.order.length) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnPreferences));
    } catch {
      // Preferences remain usable for the current session when storage is unavailable.
    }
  }, [columnPreferences, storageKey]);

  const orderedColumns = useMemo(() => {
    const order = columnPreferences.order.length ? columnPreferences.order : defaultColumnOrder;
    return order.map((id) => columnsById.get(id)).filter(Boolean);
  }, [columnPreferences.order, columnsById, defaultColumnOrder]);

  const visibleColumns = useMemo(() => orderedColumns
    .filter((column) => !columnPreferences.hidden.includes(column.id))
    .map((column) => {
      const minWidth = columnMinWidth(column);
      const preferredWidth = Number(columnPreferences.widths?.[column.id] || column.width || minWidth);
      const maxWidth = Number(column.maxWidth || 0);
      return {
        ...column,
        width: maxWidth > 0
          ? Math.min(maxWidth, Math.max(minWidth, preferredWidth))
          : Math.max(minWidth, preferredWidth),
      };
    }), [columnPreferences.hidden, columnPreferences.widths, orderedColumns]);

  const processedItems = useMemo(() => {
    const activeViewDefinition = views.find((view) => view.id === activeView);
    const normalizedQuery = normalizeText(search);
    let next = Array.isArray(items) ? [...items] : [];

    if (typeof activeViewDefinition?.predicate === 'function') {
      next = next.filter(activeViewDefinition.predicate);
    }

    if (normalizedQuery) {
      const searchableColumns = columns.filter((column) => column.searchable !== false);
      next = next.filter((item) => searchableColumns.some((column) => (
        normalizeText(getColumnValue(column, item)).includes(normalizedQuery)
      )));
    }

    const activeFilters = Object.entries(filters)
      .map(([id, value]) => ({ id, ...value }))
      .filter((filter) => {
        if (filter.startDate || filter.endDate) return true;
        if (Array.isArray(filter.values)) return filter.values.length > 0;
        return filter.value !== '' && filter.value !== undefined && filter.value !== null;
      });

    if (activeFilters.length) {
      next = next.filter((item) => activeFilters.every((filter) => (
        matchesFilter(item, filter, definitionsById, columnsById)
      )));
    }

    if (sort?.columnId) {
      const column = columnsById.get(sort.columnId);
      if (column) {
        const direction = sort.direction === 'desc' ? -1 : 1;
        next.sort((left, right) => {
          const leftValue = getColumnValue(column, left);
          const rightValue = getColumnValue(column, right);
          const leftDate = column.type === 'date' ? new Date(leftValue).getTime() : Number.NaN;
          const rightDate = column.type === 'date' ? new Date(rightValue).getTime() : Number.NaN;

          if (!Number.isNaN(leftDate) || !Number.isNaN(rightDate)) {
            const safeLeft = Number.isNaN(leftDate) ? 0 : leftDate;
            const safeRight = Number.isNaN(rightDate) ? 0 : rightDate;
            return (safeLeft - safeRight) * direction;
          }

          if (typeof leftValue === 'number' || typeof rightValue === 'number') {
            return ((Number(leftValue) || 0) - (Number(rightValue) || 0)) * direction;
          }

          return String(leftValue ?? '').localeCompare(String(rightValue ?? ''), undefined, {
            numeric: true,
            sensitivity: 'base',
          }) * direction;
        });
      }
    }

    return next;
  }, [activeView, columns, columnsById, definitionsById, filters, items, search, sort, views]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeView, filters, pageSize, search, sort]);

  const totalPages = Math.max(1, Math.ceil(processedItems.length / pageSize));

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return processedItems.slice(start, start + pageSize);
  }, [currentPage, pageSize, processedItems]);

  const activeFilterList = useMemo(() => (
    Object.entries(filters)
      .map(([id, value]) => ({ id, definition: definitionsById.get(id), ...value }))
      .filter((filter) => {
        if (filter.startDate || filter.endDate) return true;
        if (Array.isArray(filter.values)) return filter.values.length > 0;
        return filter.value !== '' && filter.value !== undefined && filter.value !== null;
      })
  ), [definitionsById, filters]);

  const setFilter = useCallback((id, value) => {
    setFilters((previous) => ({ ...previous, [id]: value }));
  }, []);

  const clearFilter = useCallback((id) => {
    setFilters((previous) => {
      const next = { ...previous };
      delete next[id];
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => setFilters({}), []);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((previous) => previous.includes(id)
      ? previous.filter((selectedId) => selectedId !== id)
      : [...previous, id]);
  }, []);

  useEffect(() => {
    const validIds = new Set(items.map(getRowId));
    setSelectedIds((previous) => {
      const next = previous.filter((id) => validIds.has(id));
      return next.length === previous.length ? previous : next;
    });
  }, [getRowId, items]);

  const allProcessedSelected = processedItems.length > 0 && processedItems.every((item) => (
    selectedIds.includes(getRowId(item))
  ));

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((previous) => {
      const ids = processedItems.map(getRowId);
      const allSelected = ids.length > 0 && ids.every((id) => previous.includes(id));
      return allSelected ? previous.filter((id) => !ids.includes(id)) : [...new Set([...previous, ...ids])];
    });
  }, [getRowId, processedItems]);

  const applyColumnPreferences = useCallback((preferences) => {
    setColumnPreferences({
      order: Array.isArray(preferences.order) ? preferences.order : defaultColumnOrder,
      hidden: Array.isArray(preferences.hidden)
        ? preferences.hidden.filter((id) => columnsById.get(id)?.hideable !== false)
        : [],
      pinned: preferences.pinned || null,
      widths: preferences.widths && typeof preferences.widths === 'object' ? preferences.widths : {},
    });
  }, [columnsById, defaultColumnOrder]);

  const restoreDefaultColumns = useCallback(() => {
    setColumnPreferences({
      order: defaultColumnOrder,
      hidden: defaultHiddenColumns,
      pinned: defaultPinnedColumn,
      widths: {},
    });
  }, [defaultColumnOrder, defaultHiddenColumns, defaultPinnedColumn]);

  const setColumnWidth = useCallback((columnId, width) => {
    const column = columnsById.get(columnId);
    if (!column) return;
    const minWidth = columnMinWidth(column);
    const maxWidth = Number(column.maxWidth || 0);
    const requestedWidth = Math.max(minWidth, Math.round(Number(width) || minWidth));
    const nextWidth = maxWidth > 0 ? Math.min(maxWidth, requestedWidth) : requestedWidth;

    setColumnPreferences((previous) => ({
      ...previous,
      widths: { ...(previous.widths || {}), [columnId]: nextWidth },
    }));
  }, [columnsById]);

  return {
    search,
    setSearch,
    activeView,
    setActiveView,
    filters,
    setFilters,
    setFilter,
    clearFilter,
    clearFilters,
    activeFilterList,
    sort,
    setSort,
    selectedIds,
    setSelectedIds,
    toggleSelected,
    toggleSelectAll,
    allProcessedSelected,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    processedItems,
    pageItems,
    orderedColumns,
    visibleColumns,
    columnPreferences,
    applyColumnPreferences,
    restoreDefaultColumns,
    setColumnWidth,
  };
}

export default useCrmListState;
